import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ProductInfo } from "./AllProducts";
import { Add, Remove } from "@mui/icons-material";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { SERVER_BASE_URL } from "./Config";

export const ProductPage = () => {
  const [selectedStrengthIndex, setSelectedStrengthIndex] = useState(0);
  const [product, setProduct] = useState<ProductInfo>();
  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  const { id } = useParams();
  const productId = id ? parseInt(id) : undefined;

  const loadProduct = async () => {
    const providerToken = localStorage.getItem("providerToken");
    const response = await fetch(`${SERVER_BASE_URL}/product/${productId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${providerToken}`,
      },
    });
    const data = await response.json();
    setProduct(data);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadProduct();
  }, []);

  if (!product) {
    return <div>Product not found!</div>;
  }

  const {
    name,
    administrationMethod,
    imageUrl,
    description,
    howItWorks,
    sideEffects,
    storage,
    pregnancy,
    drugInteractions,
  } = product;

  const handleExpand = (section: string) => {
    setExpandedSection((prevSection) =>
      prevSection === section ? false : section,
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={5} justifyContent="center">
          <Grid item xs={12} md={3} display="flex" justifyContent="center">
            <Box
              sx={{
                width: { xs: 160, md: 200 },
                height: { xs: 284, md: 356 }, // 9:16 aspect ratio
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <img
                src={imageUrl}
                alt={name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 8,
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Box sx={{ textAlign: "left", mb: 3 }}>
              {/* Back to Products Link */}
              <Typography
                component={Link}
                to="/"
                variant="caption"
                sx={{
                  color: "text.secondary",
                  textDecoration: "underline",
                  display: "inline-block",
                  mb: 1,
                }}
              >
                Back to Products
              </Typography>

              {/* Product Title */}
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {name}
              </Typography>

              {/* Dosage Form */}
              <Typography
                color="text.secondary"
                sx={{ fontSize: "0.875rem", mb: 0.5, mt: -1 }}
              >
                Dosage Form: {administrationMethod}
              </Typography>

              {/* Retail Price */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" sx={{ mr: 1, color: "text.secondary" }}>
                  Retail Price:
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "text.primary", mr: 1 }}
                >
                  {(
                    product["strengths"][selectedStrengthIndex]["price"] / 100
                  ).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </Typography>
                <Tooltip title="This is a suggested retail price. Actual cost may vary based on insurance coverage, discount programs, or other pricing arrangements." enterTouchDelay={0} leaveTouchDelay={3000}>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Provider Price */}
              {product["strengths"][selectedStrengthIndex]["provider_price"] != null && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="h6" sx={{ mr: 1, color: "text.secondary" }}>
                    Provider Price:
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: "text.primary" }}
                  >
                    {(
                      product["strengths"][selectedStrengthIndex]["provider_price"]! / 100
                    ).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </Typography>
                </Box>
              )}

            </Box>

            {/* Dosage Chart */}
            {product["strengths"].length >= (isMobile ? 4 : 6) ? (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Strength</InputLabel>
                <Select
                  value={selectedStrengthIndex}
                  label="Select Strength"
                  onChange={(e) => setSelectedStrengthIndex(Number(e.target.value))}
                >
                  {product["strengths"].map((item, index) => (
                    <MenuItem key={index} value={index}>
                      {[item["strength1"], item["strength2"], item["size"]]
                        .filter(Boolean)
                        .join(" · ")}{" "}
                      —{" "}
                      {(item["price"] / 100).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: 1,
                  borderRadius: 1,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                {product["strengths"].map((item, index) => (
                  <Button
                    key={index}
                    variant={
                      index === selectedStrengthIndex ? "contained" : "outlined"
                    }
                    onClick={() => setSelectedStrengthIndex(index)}
                    sx={{
                      border: "1px solid",
                      borderColor:
                        index === selectedStrengthIndex
                          ? "primary.main"
                          : "rgba(0, 0, 0, 0.12)",
                      color:
                        index === selectedStrengthIndex
                          ? "white"
                          : "text.primary",
                      backgroundColor:
                        index === selectedStrengthIndex
                          ? "primary.main"
                          : "white",
                      borderRadius: 0,
                      padding: "12px",
                      fontWeight:
                        index === selectedStrengthIndex ? "bold" : "normal",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor:
                          index === selectedStrengthIndex
                            ? "primary.dark"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {item["strength1"]}
                    <br />
                    {item["strength2"]}
                    <br />
                    {item["size"]}
                    <br />
                    {(item["price"] / 100).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </Button>
                ))}
              </Box>
            )}

            {/* Prescribe Button */}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                fontWeight: "bold",
                padding: "12px 0",
                fontSize: "1rem",
                mt: 2,
                borderRadius: 2,
              }}
              component={Link}
              to={`/checkout/p/${product.id}/s/${selectedStrengthIndex}`}
            >
              Prescribe Now
            </Button>
          </Grid>

          {/* About this Drug Section */}
          <Grid item xs={12}>
            <Typography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              align="center"
              sx={{ mt: 6 }}
            >
              About this drug
            </Typography>
            <Container maxWidth="md" sx={{ textAlign: "left", px: { xs: 0, sm: 3 } }}>
              <Divider
                sx={{
                  borderColor: "rgba(0, 0, 0, 0.12)",
                  borderBottomWidth: "1px",
                }}
              />

              {[
                { title: "General Information", content: description },
                { title: "How it works", content: howItWorks },
                { title: "Side Effects", content: sideEffects },
                { title: "Storage", content: storage },
                { title: "Pregnancy Information", content: pregnancy },
                { title: "Drug Interactions", content: drugInteractions },
              ].map((section, index) => (
                <React.Fragment key={index}>
                  <Accordion
                    disableGutters
                    elevation={0}
                    expanded={expandedSection === section.title}
                    onChange={() => handleExpand(section.title)}
                    sx={{ "&:before": { display: "none" } }}
                  >
                    <AccordionSummary
                      expandIcon={
                        expandedSection === section.title ? (
                          <Remove
                            sx={{ fontSize: "1.2rem", color: "#1976d2" }}
                          />
                        ) : (
                          <Add sx={{ fontSize: "1.2rem", color: "#1976d2" }} />
                        )
                      }
                      sx={{
                        flexDirection: "row-reverse",
                        "& .MuiAccordionSummary-content": {
                          marginLeft: 1,
                        },
                      }}
                    >
                      <Typography fontWeight="bold">{section.title}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography sx={{ whiteSpace: 'pre-line' }}>{section.content}</Typography>
                    </AccordionDetails>
                  </Accordion>
                  <Divider
                    sx={{
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      borderBottomWidth: "1px",
                    }}
                  />
                </React.Fragment>
              ))}
            </Container>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductPage;
